import { computeZScoresForText } from "./features";

export interface ModelWeights {
  burstiness: number;
  avgSentenceLength: number;
  lexicalDiversity: number;
  repetitionScore: number;
  entropy: number;
  punctuationRatio: number;
  avgWordLength: number;
  calibrationBias?: number;
  decisionThreshold?: number;
  [key: string]: number | undefined;
}

export const DEFAULT_CLIENT_WEIGHTS = {
  burstiness: 0.2172,
  avgSentenceLength: 0.2657,
  lexicalDiversity: 0.0202,
  repetitionScore: 0.2576,
  entropy: 0.0379,
  punctuationRatio: 0.0501,
  avgWordLength: 0.1514
};

export interface TrainingRow {
  text: string;
  label: 0 | 1;
  isUserFlagged?: boolean;
  sampleWeight?: number;
}

export interface ThresholdSweepPoint {
  threshold: number;
  f1: number;
  precision: number;
  recall: number;
  accuracy: number;
}

export interface TrainingSummaryData {
  sampleCount: number;
  nHuman: number;
  nAi: number;
  flaggedCount?: number;
  finalF1: number;
  chosenThreshold: number; // e.g. 0.52
  chosenThresholdPercent: number; // e.g. 52
  finalLoss: string;
  finalAccuracy: number;
  precision: number;
  recall: number;
  thresholdSweep?: ThresholdSweepPoint[];
}

export interface TrainingOutput {
  weights: ModelWeights;
  history: Array<{ epoch: number; loss: string; accuracy: number }>;
  summary: TrainingSummaryData;
  finalLoss: string;
  finalAccuracy: number;
}

/**
 * Trains model weights via inverse-class-frequency-weighted logistic regression
 * with 75x priority weighting on user-flagged corrections and L2 regularization.
 * Features are pre-extracted and cached once before epochs.
 * Runs a threshold sweep (0.05 to 0.95, step 0.01) maximizing F1 score,
 * and auto-sets the decisionThreshold on the calibrated ModelWeights.
 */
export function trainModelWeightedLogistic(
  samples: TrainingRow[],
  initialWeights: ModelWeights,
  options?: {
    lambda?: number;
    epochs?: number;
    lr?: number;
  }
): TrainingOutput {
  const lambda = options?.lambda ?? 0.005;
  const lr = options?.lr ?? 0.12;
  const rawEpochs = options?.epochs ?? 2000;

  // Filter rows: only non-empty text and label in {0, 1}
  const validSamples = samples.filter((s) => {
    if (!s) return false;
    const text = typeof s.text === "string" ? s.text.trim() : String(s.text || "").trim();
    return text.length > 0 && (s.label === 0 || s.label === 1);
  });

  const N = validSamples.length;
  const nHuman = validSamples.filter((s) => s.label === 0).length;
  const nAi = validSamples.filter((s) => s.label === 1).length;
  const flaggedCount = validSamples.filter((s) => s.isUserFlagged || (s.sampleWeight && s.sampleWeight > 1)).length;

  // Adaptive epoch scaling for large datasets
  const epochs = N > 3000 ? Math.min(rawEpochs, 350) : rawEpochs;

  if (N === 0) {
    return {
      weights: initialWeights,
      history: [],
      summary: {
        sampleCount: 0,
        nHuman: 0,
        nAi: 0,
        flaggedCount: 0,
        finalF1: 0,
        chosenThreshold: (initialWeights.decisionThreshold || 50) / 100,
        chosenThresholdPercent: initialWeights.decisionThreshold || 50,
        finalLoss: "0.0000",
        finalAccuracy: 100,
        precision: 0,
        recall: 0
      },
      finalLoss: "0.0000",
      finalAccuracy: 100
    };
  }

  // Inverse-Class-Frequency weights
  const wHuman = nHuman > 0 ? N / (2 * nHuman) : 1;
  const wAi = nAi > 0 ? N / (2 * nAi) : 1;

  // Precompute and cache feature Z-score vectors for all samples
  // User-flagged corrections receive a 75x priority weight multiplier so they actively shape model calibration
  let totalSampleWeight = 0;
  const dataset = validSamples.map((s) => {
    const x = computeZScoresForText(s.text);
    const classWeight = s.label === 1 ? wAi : wHuman;
    const priorityMultiplier = s.sampleWeight ?? (s.isUserFlagged ? 75 : 1);
    const weight = classWeight * priorityMultiplier;
    totalSampleWeight += weight;
    return { x, y: s.label, weight };
  });
  const normDenominator = totalSampleWeight > 0 ? totalSampleWeight : N;

  const w = [
    initialWeights.burstiness ?? DEFAULT_CLIENT_WEIGHTS.burstiness,
    initialWeights.avgSentenceLength ?? DEFAULT_CLIENT_WEIGHTS.avgSentenceLength,
    initialWeights.lexicalDiversity ?? DEFAULT_CLIENT_WEIGHTS.lexicalDiversity,
    initialWeights.repetitionScore ?? DEFAULT_CLIENT_WEIGHTS.repetitionScore,
    initialWeights.entropy ?? DEFAULT_CLIENT_WEIGHTS.entropy,
    initialWeights.punctuationRatio ?? DEFAULT_CLIENT_WEIGHTS.punctuationRatio,
    initialWeights.avgWordLength ?? DEFAULT_CLIENT_WEIGHTS.avgWordLength
  ];
  // Normalize initial weights to sum to 1.0 to match detectClient.ts inference formula
  const initSum = w.reduce((acc, v) => acc + v, 0) || 1.0;
  for (let j = 0; j < 7; j++) {
    w[j] = w[j] / initSum;
  }

  let b = initialWeights.calibrationBias ?? 0;

  const sigmoid = (z: number) => 1 / (1 + Math.exp(-Math.max(-30, Math.min(30, z))));
  const history: Array<{ epoch: number; loss: string; accuracy: number }> = [];

  let finalLoss = 0;
  let finalAccuracy = 0;

  // Priority-weighted logistic gradient descent aligned with detectClient.ts (logOdds = b + 3.2 * weightedZ)
  for (let epoch = 1; epoch <= epochs; epoch++) {
    let weightedBceLoss = 0;
    let weightedCorrect = 0;
    const dw = [0, 0, 0, 0, 0, 0, 0];
    let db = 0;

    for (let i = 0; i < N; i++) {
      const sample = dataset[i];
      let weightedZ = 0;
      for (let j = 0; j < 7; j++) {
        weightedZ += w[j] * sample.x[j];
      }
      const logOdds = b + weightedZ * 3.2;
      const p = sigmoid(logOdds);

      const predLabel = p >= 0.5 ? 1 : 0;
      if (predLabel === sample.y) {
        weightedCorrect += sample.weight;
      }

      const eps = 1e-15;
      const bce = -(sample.y * Math.log(p + eps) + (1 - sample.y) * Math.log(1 - p + eps));
      weightedBceLoss += sample.weight * bce;

      const error = sample.weight * (p - sample.y);
      for (let j = 0; j < 7; j++) {
        dw[j] += error * sample.x[j] * 3.2;
      }
      db += error;
    }

    // Weight updates with L2 Regularization and simplex normalization (sum(w) = 1.0)
    let wSum = 0;
    for (let j = 0; j < 7; j++) {
      const gradW = dw[j] / normDenominator + lambda * (w[j] - 1 / 7);
      w[j] -= lr * gradW;
      w[j] = Math.max(0.015, Math.min(0.85, w[j]));
      wSum += w[j];
    }
    if (wSum > 0) {
      for (let j = 0; j < 7; j++) {
        w[j] = w[j] / wSum;
      }
    }

    // Bias update
    const gradB = db / normDenominator;
    b -= lr * gradB;
    b = Math.max(-4.5, Math.min(4.5, b));

    let l2Sum = 0;
    for (let j = 0; j < 7; j++) {
      l2Sum += w[j] * w[j];
    }
    const currentLoss = weightedBceLoss / normDenominator + 0.5 * lambda * l2Sum;
    const currentAccuracy = (weightedCorrect / normDenominator) * 100;

    finalLoss = currentLoss;
    finalAccuracy = currentAccuracy;

    if (epoch === 1 || epoch === epochs || epoch % Math.max(1, Math.floor(epochs / 10)) === 0) {
      history.push({
        epoch,
        loss: currentLoss.toFixed(4),
        accuracy: parseFloat(currentAccuracy.toFixed(1))
      });
    }
  }

  // Pre-calculate final prediction probabilities across all samples
  const samplePredictions = dataset.map((sample) => {
    let weightedZ = 0;
    for (let j = 0; j < 7; j++) {
      weightedZ += w[j] * sample.x[j];
    }
    return {
      p: sigmoid(b + weightedZ * 3.2),
      y: sample.y,
      weight: sample.weight
    };
  });

  // Threshold sweep (0.05 to 0.95, step 0.01) maximizing weighted F1 score
  let bestThreshold = 0.50;
  let bestF1 = -1;
  let bestPrecision = 0;
  let bestRecall = 0;
  const sweepPoints: ThresholdSweepPoint[] = [];

  for (let step = 5; step <= 95; step += 1) {
    const t = Math.round(step) / 100;
    let tp = 0;
    let fp = 0;
    let fn = 0;
    let tn = 0;

    for (let i = 0; i < N; i++) {
      const sp = samplePredictions[i];
      const sw = sp.weight;
      const isPositive = sp.p >= t;
      if (isPositive && sp.y === 1) tp += sw;
      else if (isPositive && sp.y === 0) fp += sw;
      else if (!isPositive && sp.y === 1) fn += sw;
      else if (!isPositive && sp.y === 0) tn += sw;
    }

    const precision = tp + fp > 0 ? tp / (tp + fp) : 0;
    const recall = tp + fn > 0 ? tp / (tp + fn) : 0;
    const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
    const acc = (tp + tn) / normDenominator;

    sweepPoints.push({
      threshold: t,
      f1: parseFloat(f1.toFixed(4)),
      precision: parseFloat(precision.toFixed(4)),
      recall: parseFloat(recall.toFixed(4)),
      accuracy: parseFloat(acc.toFixed(4))
    });

    if (f1 > bestF1) {
      bestF1 = f1;
      bestThreshold = t;
      bestPrecision = precision;
      bestRecall = recall;
    } else if (Math.abs(f1 - bestF1) < 1e-6) {
      if (Math.abs(t - 0.50) < Math.abs(bestThreshold - 0.50)) {
        bestThreshold = t;
        bestPrecision = precision;
        bestRecall = recall;
      }
    }
  }

  if (bestF1 < 0) {
    bestF1 = 0;
    bestThreshold = 0.50;
  }

  const chosenThresholdPercent = Math.round(bestThreshold * 100);

  const updatedWeights: ModelWeights = {
    ...initialWeights,
    burstiness: parseFloat(w[0].toFixed(4)),
    avgSentenceLength: parseFloat(w[1].toFixed(4)),
    lexicalDiversity: parseFloat(w[2].toFixed(4)),
    repetitionScore: parseFloat(w[3].toFixed(4)),
    entropy: parseFloat(w[4].toFixed(4)),
    punctuationRatio: parseFloat(w[5].toFixed(4)),
    avgWordLength: parseFloat(w[6].toFixed(4)),
    calibrationBias: parseFloat(b.toFixed(2)),
    decisionThreshold: chosenThresholdPercent
  };

  const summary: TrainingSummaryData = {
    sampleCount: N,
    nHuman,
    nAi,
    flaggedCount,
    finalF1: parseFloat(bestF1.toFixed(4)),
    chosenThreshold: bestThreshold,
    chosenThresholdPercent,
    finalLoss: finalLoss.toFixed(4),
    finalAccuracy: parseFloat(finalAccuracy.toFixed(1)),
    precision: parseFloat(bestPrecision.toFixed(4)),
    recall: parseFloat(bestRecall.toFixed(4)),
    thresholdSweep: sweepPoints
  };

  return {
    weights: updatedWeights,
    history,
    summary,
    finalLoss: finalLoss.toFixed(4),
    finalAccuracy: parseFloat(finalAccuracy.toFixed(1))
  };
}
